import React, { useState } from "react"
import Button from "aldoo-ra/Button"
import Typography from "aldoo-ra/Typography"
import Chip from "./Chip"
import { BuildingStorefrontIcon, MapPinIcon } from "@heroicons/react/24/outline"
import OfficeMapDialog from "../checkout/shipping/OfficeMapDialog"
import useT from "../hooks/useT"
import useSiteLanguage from "../hooks/useSiteLanguage"
import { authorUrl } from "../utils/contentRoutes"
import { useCategories } from "../hooks/useCatalogData"
import { formatDate, isOutOfStock } from "../utils/productUtils"
import { useStoreExtensionText } from "aldoo-ra/Store/extensions/texts"

const formatWeight = (weight) => {
  if (!weight) return null
  if (typeof weight === "string" && (weight.includes("g") || weight.includes("kg"))) return weight
  const n = typeof weight === "string" ? parseFloat(weight) : weight
  if (isNaN(n)) return weight
  return n >= 1000 ? `${(n / 1000).toFixed(1)}kg` : `${n}g`
}

/**
 * One line of the details: the label on the left, the value on the right, a
 * dotted leader between them. A long value wraps and stays right-aligned; the
 * leader sits on the first line, level with the label.
 */
function PropertyRow({ label, value, link }) {
  if (!value) return null
  const displayLabel = label ? label.replace(/:\s*$/, "") : ""
  return (
    <div className="flex flex-row items-start gap-2 py-2 leading-[1.6]" data-testid="detail-row">
      <Typography 
        variant="txt" 
        className="shrink-0 text-text"
      >
        {displayLabel}
      </Typography>

      {/* the leader: fills the gap, drawn on the first line's baseline */}
      <span 
        aria-hidden="true" 
        className="flex-1 min-w-[24px] border-b-2 border-dotted border-neutral_2 mt-[14px]" 
      />

      <Typography 
        variant="txt" 
        className="max-w-[62%] text-right text-text"
      >
        {link ? (
          <Button variant="link" href={link}>
            {value}
          </Button>
        ) : (
          value
        )}
      </Typography>
    </div>
  )
}

/**
 * An address the product names (its "address" field: a gallery, a venue — "София, ул. Дунав 11"): the line, and
 * "See on map", which opens the map dialog the couriers' offices open in (a map of the address).
 */
function AddressRow({ label, address, t, language }) {
  const [open, setOpen] = useState(false)
  if (!address) return null
  return (
    <div className="py-2 leading-[1.6]" data-testid="detail-address">
      {/* the label, the leader, the address and its map button on one line */}
      <div className="flex flex-row items-center gap-2">
        <Typography variant="txt" className="shrink-0 text-text">
          {label}
        </Typography>
        <span aria-hidden="true" className="flex-1 min-w-[24px] border-b-2 border-dotted border-neutral_2 self-end mb-[10px]" />
        <Typography variant="txt" className="max-w-[50%] text-right text-text">
          {address}
        </Typography>
        <button type="button" onClick={() => setOpen(true)} className="shrink-0 inline-flex items-center gap-1.5 h-9 px-4 rounded-full border border-bordered text-[14px] font-semibold text-text hover:bg-neutral" data-testid="detail-address-map">
          <MapPinIcon className="w-4 h-4" />
          {t`See on map`}
        </button>
      </div>
      <OfficeMapDialog isOpen={open} onClose={() => setOpen(false)} providerId={null} office={{ name: label, address }} language={language} />
    </div>
  )
}

/**
 * "Details" tab: the rows listed in bookSettings.storePageFields — the fixed
 * ones (author, publisher, availability…) and the product's own fields
 * (`product.fields`, already in the page's language), labelled by its
 * profile's presets (the content types list entry of the product's profile),
 * or by their key when the profile has no label for them.
 */
export default function StoreProductProperties({ product, profile }) {
  const t = useT()
  const language = useSiteLanguage()
  const storeText = useStoreExtensionText(language)
  const { list: categories } = useCategories()
  const fields = product?.fields && typeof product.fields === "object" ? product.fields : {}
  // the "series" field is a reference to a series term (its slug): shown by
  // the term's name, linked to its page; a text left from before shows as it is
  const series = fields.series ? categories.find((c) => c.categoryId === fields.series && c.kind === "series") : null
  const presets = profile?.fieldPresets && typeof profile.fieldPresets === "object" ? profile.fieldPresets : {}
  // the address field, by its key in either language ("address", "Адрес"): the key and the line
  const addressKey = Object.keys(fields).find((key) => /^(address|адрес)$/i.test(key.trim()) && String(fields[key] || "").trim())
  // a key reads as words when the type gives it no label: coverType → Cover type
  const labelOf = (key) => presets[key] || key.replace(/[_-]+/g, " ").replace(/([a-z])([A-Z])/g, "$1 $2").replace(/^./, (c) => c.toUpperCase())
  const raw = product?.bookSettings?.storePageFields || []
  const rows = Array.isArray(raw) ? raw : Object.keys(raw).filter((k) => raw[k])
  const names = (list) => (list?.length ? list.map(({ name }) => name).join(" | ") : null)
  const outOfStock = isOutOfStock(product)

  const renderers = {
    author: () =>
      product?.author?.map((author, i) => (
        <PropertyRow key={`author-${i}`} label={i === 0 ? t`Author` : ""} value={author.name} link={authorUrl(author)} />
      )),
    translator: () => <PropertyRow key="translator" label={t`Translator`} value={names(product?.translator)} />,
    illustrator: () => <PropertyRow key="illustrator" label={t`Illustrator`} value={names(product?.illustrator)} />,
    narrator: () => <PropertyRow key="narrator" label={t`Narrator`} value={names(product?.narrator)} />,
    publisher: () => <PropertyRow key="publisher" label={t`Published by`} value={product.publisher ? t(product.publisher) : null} />,
    // the product's own publish date and language are its fields: a row shows
    // only what the product says, never a stand-in (the store's creation
    // time, the product's language list) — a bag has no language
    publishDate: () => <PropertyRow key="publishDate" label={labelOf("publishDate")} value={fields.publishDate ? formatDate(fields.publishDate) : null} />,
    created: () => <PropertyRow key="created" label={t`Publish date`} value={formatDate(product.created)} />,
    // the fields with a special reading: the weight is grams
    language: () => <PropertyRow key="language" label={labelOf("language")} value={fields.language || null} />,
    weight: () => <PropertyRow key="weight" label={labelOf("weight")} value={formatWeight(fields.weight)} />,
    deliverableTypes: () => {
      const types = [...new Set((product?.deliverables || []).map((d) => d.originalFileName?.split(".").pop()?.toLowerCase() || d.deliverableType).filter(Boolean))]
      if (!types.length) return null
      return (
        <div key="deliverableTypes" className="flex flex-row flex-wrap items-center gap-2 mb-2 text-[14px] md:text-[16px]">
          <span className="text-text_secondary">{t`Available as`}:</span>
          {types.map((type) => (
            <Chip key={type} tone="neutral" size="sm" label={type.toUpperCase()} />
          ))}
        </div>
      )
    },
    deliverableCount: () => {
      const count = product?.deliverables?.length
      return count ? <PropertyRow key="deliverableCount" label={t`Files`} value={String(count)} /> : null
    },
    supportedPlatforms: () => null,
    // where it is (a gallery, a venue): the address, and a map of it
    address: () => <AddressRow key="address" label={presets[addressKey] || t`Address`} address={addressKey ? String(fields[addressKey]).trim() : ""} t={t} language={language} />,
  }

  // the admin picks which rows show; the availability chip is always the
  // first of them, whatever order the list was saved in
  const showAvailability = rows.includes("availableForPurchase")
  // the product's code (identifiers) leads the rows; a "sku" field is that same code
  const ordered = rows.filter((key) => key !== "availableForPurchase" && !(key === "sku" && product?.sku))
  // an address the product names always shows, with its map (a buyer needs to know where to go), listed or not
  if (addressKey && !ordered.includes("address")) ordered.push("address")
  // the field shows once: as the address row, not again as a plain one under its own key
  if (addressKey && addressKey !== "address") ordered.splice(0, ordered.length, ...ordered.filter((key) => key !== addressKey))

  const row = (key) => {
    if (renderers[key]) return renderers[key]()
    if (key === "series" && series) return <PropertyRow key="series" label={labelOf("series")} value={series.name} link={`/category/${series.categoryId}`} />
    // any other row is one of the product's fields
    if (fields[key] === undefined || fields[key] === null || fields[key] === "") return null
    return <PropertyRow key={key} label={labelOf(key)} value={String(fields[key])} />
  }
  return (
    <div className="mb-6">
      {/* on top: whether it can be bought, and who sells it (the merchant, as the search and the cart name it) */}
      {(showAvailability || product?.publisher) && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mb-2">
          {showAvailability && <Chip tone={outOfStock ? "error" : "success"} label={outOfStock ? t`Out of stock` : t`Available`} className={outOfStock ? "" : "bg-[#2e7d32] border-[#2e7d32]"} />}
          {product?.publisher && (
            <span className="inline-flex items-center gap-1.5 text-[15px] text-text_secondary" data-testid="detail-merchant">
              <BuildingStorefrontIcon className="w-5 h-5 shrink-0" />
              <span>
                {t`Merchant`}: <span className="text-text font-semibold">{t(product.publisher)}</span>
              </span>
            </span>
          )}
        </div>
      )}
      {product?.sku && <PropertyRow key="sku" label={storeText("sku")} value={product.sku} />}
      {ordered.map(row)}
    </div>
  )
}
